import { useCallback, useEffect, useState } from "react";
import { useGlobalAPIQueryContext } from "@dev2_techwalnut/tw-api-query-hook";
import Swal from "sweetalert2";
import "./LiveSite.css";

const unwrap = (response) =>
    response?.data?.results?.data ?? response?.data?.data ?? response?.data;

const LiveSitePipelineStages = () => {
    const { apiClient } = useGlobalAPIQueryContext();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        const response = await apiClient.get("/api/live-site/pipeline-stages/");
        setRows(unwrap(response) || []);
    }, [apiClient]);

    useEffect(() => {
        setLoading(true);
        load()
            .catch((error) => {
                Swal.fire(
                    "Could not load pipeline stages",
                    error?.response?.data?.results?.message || error.message,
                    "error"
                );
            })
            .finally(() => setLoading(false));
    }, [load]);

    const updateRow = (index, patch) => {
        setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    };

    const move = (index, direction) => {
        const next = index + direction;
        setRows((current) => {
            if (next < 0 || next >= current.length) return current;
            const copy = [...current];
            const [item] = copy.splice(index, 1);
            copy.splice(next, 0, item);
            return copy;
        });
    };

    const addStage = () => {
        setRows((current) => [...current, { key: "", stageName: "New stage", shape: "circle" }]);
    };

    const removeStage = (index) => {
        setRows((current) => current.filter((_, i) => i !== index));
    };

    const save = async () => {
        if (rows.length === 0) {
            Swal.fire("Cannot save", "Keep at least one pipeline stage.", "warning");
            return;
        }
        setSaving(true);
        try {
            const response = await apiClient.put("/api/live-site/pipeline-stages/", {
                stages: rows.map((row) => ({
                    key: row.key || undefined,
                    stageName: row.stageName,
                    shape: row.shape,
                })),
            });
            setRows(unwrap(response) || []);
            Swal.fire({
                icon: "success",
                title: "Pipeline stages saved",
                text: "Name, shape, and order now apply to all machines. Existing Sorted/Implemented checks are kept.",
                timer: 3200,
                showConfirmButton: false,
            });
        } catch (error) {
            Swal.fire("Save failed", error?.response?.data?.results?.message || error.message, "error");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <p className="text-soft">Loading pipeline stages…</p>;
    }

    return (
        <div className="card card-inner">
            <p className="text-soft">
                Name, shape, and order apply to every machine. Sorted and Implemented checks stay as they
                were. A new block is added to existing machines. If you remove a block that is already
                Sorted or Implemented, it stays on those machines with a Removed label. Unchecked blocks
                disappear.
            </p>
            <div className="live-site-catalog-list">
                {rows.map((row, index) => (
                    <div className="live-site-catalog-row" key={row.key || `new-${index}`}>
                        <input
                            className="form-control"
                            value={row.stageName || ""}
                            onChange={(event) => updateRow(index, { stageName: event.target.value })}
                        />
                        <select
                            className="form-select"
                            value={row.shape || "circle"}
                            onChange={(event) => updateRow(index, { shape: event.target.value })}
                        >
                            <option value="circle">Circle</option>
                            <option value="rectangle">Rectangle</option>
                        </select>
                        <button type="button" className="btn btn-sm btn-outline-light" onClick={() => move(index, -1)}>
                            Up
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-light" onClick={() => move(index, 1)}>
                            Down
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeStage(index)}
                            disabled={rows.length <= 1}
                        >
                            Remove
                        </button>
                    </div>
                ))}
            </div>
            <div className="live-site-toolbar">
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={addStage}>
                    Add stage
                </button>
                <button type="button" className="btn btn-sm btn-primary" onClick={save} disabled={saving}>
                    {saving ? "Saving…" : "Save pipeline stages"}
                </button>
            </div>
        </div>
    );
};

export default LiveSitePipelineStages;
